import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import {
  KbCompanyContextItem,
  KbCompetitorItem,
  KbContentData,
  KbFindingSeverity,
  KbLandmineItem,
  KbObjectionOrFaqItem,
  KbPlaybookItem,
  KbReadinessCategory,
  KbReadinessFinding,
  KbReadinessStatus,
  KbReadinessThresholds,
  KbReadinessVerdict,
  KbSuggestionCategory
} from '../types/superadmin';
import { ErrorState, LoadingState } from './primitives';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { displayAuditText } from '../utils/kbAuditText';

type Category = 'objections' | 'faqs' | 'landmines' | 'competitors' | 'playbooks';
type AuditCategory = 'company_context' | Category;

const DEFAULT_THRESHOLDS: KbReadinessThresholds = {
  minObjections: 5,
  minFaqs: 8,
  minLandmines: 1,
  minCompetitors: 1,
  minPlaybooks: 1,
  minObjectionVariations: 2,
  minFaqVariations: 2,
  minCompetitorPoints: 1
};

const CATEGORY_ORDER: Category[] = ['objections', 'faqs', 'landmines', 'competitors', 'playbooks'];
const AUDIT_CATEGORY_ORDER: AuditCategory[] = ['company_context', ...CATEGORY_ORDER];

const CATEGORY_LABELS: Record<KbReadinessCategory, string> = {
  company_context: 'Company context',
  objections: 'Objections',
  faqs: 'FAQs',
  landmines: 'Landmines',
  competitors: 'Competitors',
  playbooks: 'Playbooks'
};

const STATUS_LABELS: Record<KbReadinessStatus, string> = {
  ready: 'Ready',
  needs_more: 'Needs detail',
  attention: 'Needs attention'
};

const STATUS_TONE: Record<KbReadinessStatus, string> = {
  ready: 'pos',
  needs_more: 'warn',
  attention: 'neg'
};

const VERDICT_LABELS: Record<KbReadinessVerdict, string> = {
  ready: 'Ready for live calls',
  almost_ready: 'Almost ready',
  needs_attention: 'Needs reviewer attention'
};

const VERDICT_TONE: Record<KbReadinessVerdict, string> = {
  ready: 'pos',
  almost_ready: 'warn',
  needs_attention: 'neg'
};

const SEVERITY_TONE: Record<KbFindingSeverity, string> = {
  critical: 'neg',
  warning: 'warn',
  info: 'info'
};

const CLEANUP_LABELS: Record<string, string> = {
  pending: 'cleanup pending',
  cleaned: 'cleaned',
  manual: 'manual',
  skipped: 'cleanup skipped',
  error: 'cleanup error'
};

const THRESHOLD_CONTROLS: Array<{ key: keyof KbReadinessThresholds; label: string }> = [
  { key: 'minObjections', label: 'Min objections' },
  { key: 'minFaqs', label: 'Min FAQs' },
  { key: 'minLandmines', label: 'Min landmines' },
  { key: 'minCompetitors', label: 'Min competitors' },
  { key: 'minPlaybooks', label: 'Min playbooks' },
  { key: 'minObjectionVariations', label: 'Obj variations' },
  { key: 'minFaqVariations', label: 'FAQ variations' },
  { key: 'minCompetitorPoints', label: 'Competitor points' }
];

function metricText(data: KbContentData, category: Category): string {
  const m = data.review.metrics;
  if (category === 'objections') return `${m.objections.total} items | ${m.objections.meetingVariations} variation-ready`;
  if (category === 'faqs') return `${m.faqs.total} items | ${m.faqs.meetingVariations} variation-ready`;
  if (category === 'landmines') return `${m.landmines.approvedUsable}/${m.landmines.active} usable`;
  if (category === 'competitors') return `${m.competitors.usable}/${m.competitors.active} positioned`;
  return `${m.playbooks.readyRoles}/${m.playbooks.activeRoles} buyer audiences ready`;
}

function canSendCategory(category: KbReadinessCategory): category is KbSuggestionCategory {
  return category !== 'company_context';
}

function ThresholdControl({
  item,
  value,
  onChange
}: {
  item: { key: keyof KbReadinessThresholds; label: string };
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="kb-threshold-control">
      <span>{item.label}</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(event) => onChange(Number.parseInt(event.target.value || '0', 10))}
      />
    </label>
  );
}

// Objection and FAQ rows share a shape; only the name of the written reply differs.
type ObjectionFaqKind = 'objection' | 'faq';

const OBJECTION_FAQ_COPY: Record<ObjectionFaqKind, { replyLabel: string; missingReply: string; itemLabel: string }> = {
  objection: { replyLabel: 'Response', missingReply: 'No response written yet.', itemLabel: 'objections' },
  faq: { replyLabel: 'Answer', missingReply: 'No answer written yet.', itemLabel: 'FAQs' }
};

// Long audits (hundreds of objections) stay scannable a page at a time.
const OBJECTION_FAQ_PAGE_SIZE = 10;

function ObjectionFaqRow({ item, kind }: { item: KbObjectionOrFaqItem; kind: ObjectionFaqKind }) {
  const [open, setOpen] = useState(false);
  const copy = OBJECTION_FAQ_COPY[kind];
  const variations = item?.variations ?? [];
  return (
    <div className="kb-review-row">
      <button
        type="button"
        className="kb-review-row-main"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="kb-review-row-title">{item.question}</span>
        <span className="kb-review-row-meta">
          {item.category && <span className="chip">{item.category}</span>}
          {item.styleCleanupStatus && item.styleCleanupStatus !== 'cleaned' && item.styleCleanupStatus !== 'manual' && (
            <span className="chip warn">{CLEANUP_LABELS[item.styleCleanupStatus] || item.styleCleanupStatus}</span>
          )}
          {variations.length > 0 && (
            <span className="chip">
              {variations.length} variation{variations.length === 1 ? '' : 's'}
            </span>
          )}
          <span className={`kb-review-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <div className="kb-review-detail">
          <div className="kb-review-detail-block">
            <div className="kb-review-mini-label">{copy.replyLabel}</div>
            <p>{item.answer || copy.missingReply}</p>
          </div>
          <div className="kb-review-detail-block">
            <div className="kb-review-mini-label">Variations ({variations.length})</div>
            {variations.length === 0 ? (
              <p>No variations yet.</p>
            ) : (
              <ul className="kb-review-variation-list">
                {variations.map((v) => (
                  <li key={v._id}>{v.question}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ObjectionFaqList({ items, kind }: { items: KbObjectionOrFaqItem[]; kind: ObjectionFaqKind }) {
  const pagination = usePagination(items, OBJECTION_FAQ_PAGE_SIZE);
  return (
    <>
      {pagination.pageItems.map((item) => (
        <ObjectionFaqRow key={item._id} item={item} kind={kind} />
      ))}
      <Pagination state={pagination} itemLabel={OBJECTION_FAQ_COPY[kind].itemLabel} />
    </>
  );
}

function LandmineRow({ item }: { item: KbLandmineItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="kb-review-row">
      <button type="button" className="kb-review-row-main" onClick={() => setOpen((v) => !v)}>
        <span className="kb-review-row-title">{item.trigger}</span>
        <span className="kb-review-row-meta">
          {item.priority && <span className="chip">{item.priority}</span>}
          <span className={'chip ' + (item.approvalStatus === 'approved' ? 'pos' : 'warn')}>
            {item.approvalStatus === 'approved' ? 'approved' : 'pending review'}
          </span>
          <span className="chip info">Protected client wording</span>
          <span className={'chip ' + (item.isActive ? 'pos' : 'neg')}>{item.isActive ? 'live' : 'inactive'}</span>
          <span className={`kb-review-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <div className="kb-review-two-col">
          <div>
            <div className="kb-review-mini-label">Can say</div>
            <ul>
              {item.canSay.length === 0 && <li>Nothing written.</li>}
              {item.canSay.map((p, i) => (
                <li key={i}>{p.point}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="kb-review-mini-label">Cannot say</div>
            <ul>
              {item.cannotSay.length === 0 && <li>Nothing written.</li>}
              {item.cannotSay.map((p, i) => (
                <li key={i}>{p.point}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyContextRow({ item }: { item: KbCompanyContextItem }) {
  const fields = [
    ['What we sell', item.whatYouSell],
    ['Target audience', item.targetAudience],
    ['How it works', item.howItWorks],
  ].filter(([, value]) => Boolean(value));

  return (
    <div className="kb-review-role-grid">
      {fields.length === 0 && item.companyContext && (
        <div className="kb-review-role-card">
          <div className="kb-review-mini-label">
            Legacy company context <span className="chip info">Protected client wording</span>
          </div>
          <div className="kb-review-row-body">{item.companyContext}</div>
        </div>
      )}
      {fields.map(([label, value]) => (
        <div key={label} className="kb-review-role-card">
          <div className="kb-review-mini-label">{label}</div>
          <div className="kb-review-row-body">{value}</div>
        </div>
      ))}
      {item.differentiators.length > 0 && (
        <div className="kb-review-role-card">
          <div className="kb-review-mini-label">Differentiators</div>
          <ul>{item.differentiators.map((value) => <li key={value}>{value}</li>)}</ul>
        </div>
      )}
      {item.certifications.length > 0 && (
        <div className="kb-review-role-card">
          <div className="kb-review-mini-label">
            Certifications <span className="chip info">Protected client wording</span>
          </div>
          <ul>{item.certifications.map((value) => <li key={value}>{value}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function CompetitorRow({ item }: { item: KbCompetitorItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="kb-review-row">
      <button type="button" className="kb-review-row-main" onClick={() => setOpen((v) => !v)}>
        <span className="kb-review-row-title">{item.name}</span>
        <span className="kb-review-row-meta">
          {item.category && <span className="chip">{item.category.replace('_', ' ')}</span>}
          <span className={'chip ' + (item.isActive ? 'pos' : 'warn')}>{item.isActive ? 'live' : 'inactive'}</span>
          <span className={`kb-review-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <>
          <div className="kb-review-two-col">
            <div>
              <div className="kb-review-mini-label">Where we win</div>
              <ul>
                {item.advantages.length === 0 && <li>Nothing written.</li>}
                {item.advantages.map((p, i) => (
                  <li key={i}>{p.point}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="kb-review-mini-label">Where they win</div>
              <ul>
                {item.weaknesses.length === 0 && <li>Nothing written.</li>}
                {item.weaknesses.map((p, i) => (
                  <li key={i}>{p.point}</li>
                ))}
              </ul>
            </div>
          </div>
          {item.talkingPoints && <p className="kb-review-row-body">{item.talkingPoints}</p>}
        </>
      )}
    </div>
  );
}

function PlaybookRow({ item }: { item: KbPlaybookItem }) {
  const [open, setOpen] = useState(false);
  const activeRoles = item.roles.filter((role) => role.isActive);
  return (
    <div className="kb-review-row">
      <button type="button" className="kb-review-row-main" onClick={() => setOpen((v) => !v)}>
        <span>
          <span className="kb-review-row-title">{item.displayName}</span>
          <span className="kb-review-row-body">
            {activeRoles.length} active role{activeRoles.length === 1 ? '' : 's'}
          </span>
        </span>
        <span className="kb-review-row-meta">
          <span className={'chip ' + (item.isActive ? 'pos' : 'warn')}>{item.isActive ? 'live' : 'inactive'}</span>
          <span className={`kb-review-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <div className="kb-review-role-grid">
          {item.roles.map((role) => (
            <div key={role._id || role.name} className="kb-review-role-card">
              <div className="kb-review-row-title">{role.displayName}</div>
              <div className="kb-review-row-body">
                {role.pitchCount} pitch | {role.questionCount} questions | {role.advancedCount} progression
              </div>
              {role.pitch.length > 0 && (
                <>
                  <div className="kb-review-mini-label">Pitch</div>
                  <ul>{role.pitch.map((value) => <li key={value}>{value}</li>)}</ul>
                </>
              )}
              {role.questions.length > 0 && (
                <>
                  <div className="kb-review-mini-label">Questions</div>
                  <ul>{role.questions.map((value) => <li key={value}>{value}</li>)}</ul>
                </>
              )}
              {role.advanced.length > 0 && (
                <>
                  <div className="kb-review-mini-label">Deal progression</div>
                  <ul>{role.advanced.map((value) => <li key={value}>{value}</li>)}</ul>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function KbContentPanel({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<KbContentData | null>(null);
  const [thresholds, setThresholds] = useState<KbReadinessThresholds>(DEFAULT_THRESHOLDS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<AuditCategory>('objections');
  const [showConfig, setShowConfig] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{ category: KbSuggestionCategory; message: string } | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSuggestionError(null);
    try {
      const res = await api.getKbContent(tenantId);
      setData(res?.data ?? null);
      if (res?.data?.review?.thresholds) setThresholds(res.data.review.thresholds);
    } catch (error: any) {
      setLoadError(error?.message || 'Could not load knowledge base content for this client.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveThresholds = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.saveKbReadinessThresholds(tenantId, thresholds);
      setData(res?.data ?? null);
      if (res?.data?.review?.thresholds) setThresholds(res.data.review.thresholds);
      setNotice('Readiness thresholds saved for this client.');
      setShowConfig(false);
    } catch (error: any) {
      setLoadError(error?.message || 'Could not save readiness thresholds.');
    } finally {
      setLoading(false);
    }
  };

  const updateThreshold = (key: keyof KbReadinessThresholds, value: number) => {
    setThresholds((prev) => ({
      ...prev,
      [key]: Number.isFinite(value) ? Math.max(0, value) : 0
    }));
  };

  const runAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    setNotice(null);
    try {
      const res = await api.runKbAuditNow(tenantId);
      if (res.data.queued) setNotice('KB audit queued. Findings will appear here when it completes.');
      if (res.data.reason === 'already_running') setNotice('A KB audit is already running for this client.');
      if (res.data.reason === 'cooldown') setNotice('A manual audit ran recently. Try again after the cooldown.');
      await load();
    } catch (error: any) {
      setAuditError(error?.message || 'Could not queue the KB audit.');
    } finally {
      setAuditLoading(false);
    }
  };

  const openFindingSuggestion = (finding: KbReadinessFinding) => {
    if (!canSendCategory(finding.category)) return;
    setSuggestion({
      category: finding.category,
      message: finding.sendSuggestion || finding.csmAction
    });
    setSuggestionError(null);
    setNotice(null);
  };

  const sendSuggestion = async () => {
    if (!suggestion || !suggestion.message.trim()) return;
    setSendingSuggestion(true);
    setNotice(null);
    setSuggestionError(null);
    try {
      await api.sendKbSuggestion(tenantId, {
        category: suggestion.category,
        message: suggestion.message.trim()
      });
      setNotice('Suggestion sent to the client admin.');
      setSuggestion(null);
    } catch (error: any) {
      setSuggestionError(error?.message || 'Could not send the suggestion.');
    } finally {
      setSendingSuggestion(false);
    }
  };

  const activeItemCount = !data
    ? 0
    : activeCategory === 'company_context'
      ? Number(Boolean(
        data.companyContext.whatYouSell ||
        data.companyContext.targetAudience ||
        data.companyContext.howItWorks ||
        data.companyContext.companyContext ||
        data.companyContext.certifications.length ||
        data.companyContext.differentiators.length
      ))
      : data.categories[activeCategory].length;

  const auditCategoryCount = (category: AuditCategory) => {
    if (!data) return 0;
    if (category !== 'company_context') return data.categories[category].length;
    return Number(Boolean(
      data.companyContext.whatYouSell ||
      data.companyContext.targetAudience ||
      data.companyContext.howItWorks ||
      data.companyContext.companyContext ||
      data.companyContext.certifications.length ||
      data.companyContext.differentiators.length
    ));
  };

  return (
    <section className="section kb-review-shell kb-product">
      <div className="section-head">
        <div>
          <h3>Knowledge base readiness</h3>
          <p className="desc">
            Live coverage and background audit findings. Client admins decide which changes are published.
          </p>
        </div>
        <div className="right">
          <button type="button" className="btn" onClick={() => setShowConfig((v) => !v)}>
            {showConfig ? 'Hide thresholds' : 'Configure thresholds'}
          </button>
          <button type="button" className={`btn${!showConfig && !suggestion ? ' btn-primary' : ''}`} disabled={auditLoading} onClick={runAudit}>
            {auditLoading ? 'Queuing audit...' : 'Run KB audit now'}
          </button>
        </div>
      </div>

      {loading && <LoadingState label="Loading knowledge base..." />}
      {!loading && loadError && <ErrorState message={loadError} />}

      {!loading && !loadError && data && (
        <>
          {notice && <div className="panel-note pos" role="status" aria-live="polite">{notice}</div>}
          {auditError && <div className="panel-note neg" role="alert">{auditError}</div>}
          {suggestionError && <div className="panel-note neg" role="alert">{suggestionError}</div>}

          <div className="kb-review-hero">
            <div>
              <span className={'status-pill ' + VERDICT_TONE[data.review.verdict]}>
                <span className="dot" />
                {VERDICT_LABELS[data.review.verdict]}
              </span>
              <h4>
                {data.review.readyChecks} of {data.review.totalChecks} readiness checks pass
              </h4>
              <p>{data.review.summary}</p>
            </div>
          </div>

          {showConfig && (
            <div className="kb-threshold-panel">
              <div className="section-head compact">
                <div>
                  <h3>Thresholds</h3>
                  <p className="desc">Tune what "ready" means for this client. These checks are shared across the team.</p>
                </div>
                <button type="button" className={`btn${!suggestion ? ' btn-primary' : ''}`} onClick={saveThresholds} disabled={loading}>
                  Save thresholds
                </button>
              </div>
              <div className="kb-threshold-grid">
                {THRESHOLD_CONTROLS.map((item) => (
                  <ThresholdControl
                    key={item.key}
                    item={item}
                    value={thresholds[item.key]}
                    onChange={(value) => updateThreshold(item.key, value)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="kb-review-grid">
            {CATEGORY_ORDER.map((category) => (
              <button
                key={category}
                type="button"
                className={'kb-review-card ' + (activeCategory === category ? 'active' : '')}
                onClick={() => setActiveCategory(category)}
              >
                <span className="kb-review-card-label">{CATEGORY_LABELS[category]}</span>
                <span className={'chip ' + STATUS_TONE[data.review.status[category]]}>
                  {STATUS_LABELS[data.review.status[category]]}
                </span>
                <span className="kb-review-card-metric">{metricText(data, category)}</span>
              </button>
            ))}
          </div>

          <div className="kb-review-main">
            <div className="kb-review-panel">
              <div className="section-head compact">
                <div>
                  <h3>Review queue</h3>
                  <p className="desc">Coverage suggestions based on the client&apos;s current setup.</p>
                </div>
              </div>
              {data.review.findings.length === 0 ? (
                <div className="empty-state">No configured gaps found.</div>
              ) : (
                <div className="kb-finding-list">
                  {data.review.findings.map((finding) => (
                    <div key={finding.id} className="kb-finding">
                      <div className="flags">
                        <span className={'chip ' + SEVERITY_TONE[finding.severity]}>{finding.severity}</span>
                        <span className="chip">{CATEGORY_LABELS[finding.category]}</span>
                      </div>
                      <strong>{finding.title}</strong>
                      <p>{finding.detail}</p>
                      <p className="kb-review-row-body">{finding.csmAction}</p>
                      {canSendCategory(finding.category) && finding.sendSuggestion && (
                        <button type="button" className="btn" onClick={() => openFindingSuggestion(finding)}>
                          Suggest to client
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="kb-review-panel">
              <div className="section-head compact">
                <div>
                  <h3>KB health findings</h3>
                  <p className="desc">Read-only audit history. Only the client admin can apply or dismiss a finding.</p>
                </div>
              </div>
              {data.audit.findings.length === 0 ? (
                <div className="empty-state">No audit findings yet.</div>
              ) : (
                <div className="kb-finding-list">
                  {data.audit.findings.map((finding) => (
                    <div key={finding._id} className="kb-finding">
                      <div className="flags">
                        <span className={'chip ' + (finding.status === 'open' ? 'warn' : 'pos')}>{finding.status}</span>
                        <span className="chip">{finding.confidence} confidence</span>
                      </div>
                      <strong>{displayAuditText(finding.title, finding.sourceItems)}</strong>
                      <p>{displayAuditText(finding.impact, finding.sourceItems)}</p>
                      <p className="kb-review-row-body">{displayAuditText(finding.recommendation, finding.sourceItems)}</p>
                      <p className="kb-review-row-body mono">
                        {finding.evidence?.totalFires || 0} fires across {finding.evidence?.distinctCalls || 0} calls
                      </p>
                      {finding.decision?.action && (
                        <p className="kb-review-row-body">
                          Client action: {finding.decision.action.replace(/_/g, ' ')}
                          {finding.decision.byEmail ? ` by ${finding.decision.byEmail}` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {data.audit.runs.length > 0 && (
                <div className="panel-note">
                  Latest run: {data.audit.runs[0].status}
                  {data.audit.runs[0].skippedUnchanged ? ', unchanged content skipped' : ''}
                </div>
              )}
            </div>
          </div>

          {suggestion && (
            <div className="user-form">
              <div className="section-head compact">
                <div>
                  <h3>Suggestion for client admin</h3>
                  <p className="desc">{CATEGORY_LABELS[suggestion.category]}</p>
                </div>
              </div>
              <label className="field">
                <span>Message</span>
                <textarea
                  value={suggestion.message}
                  onChange={(event) => setSuggestion((prev) => prev && { ...prev, message: event.target.value })}
                  rows={4}
                />
              </label>
              <div className="user-form-actions">
                <button type="button" className="btn" disabled={sendingSuggestion} onClick={() => setSuggestion(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={sendingSuggestion || !suggestion.message.trim()}
                  onClick={sendSuggestion}
                >
                  {sendingSuggestion ? 'Sending...' : 'Send suggestion'}
                </button>
              </div>
            </div>
          )}

          <div className="kb-review-panel">
            <div className="section-head compact">
              <div>
                <h3>Full KB audit</h3>
                <p className="desc">Read-only live content behind the readiness checks.</p>
              </div>
            </div>
            <div className="filter-chips" style={{ marginBottom: 14 }}>
              {AUDIT_CATEGORY_ORDER.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  aria-label={`Audit ${CATEGORY_LABELS[cat]}`}
                  className={'filter-chip ' + (activeCategory === cat ? 'on' : '')}
                  onClick={() => setActiveCategory(cat)}
                >
                  {CATEGORY_LABELS[cat]}{' '}
                  <span className="c">
                    {auditCategoryCount(cat)}
                  </span>
                </button>
              ))}
            </div>

            {activeItemCount === 0 && <div className="empty-state">Nothing here yet.</div>}
            {activeCategory === 'company_context' && activeItemCount > 0 && (
              <CompanyContextRow item={data.companyContext} />
            )}
            {activeCategory === 'objections' && (
              <ObjectionFaqList key="objections" items={data.categories.objections} kind="objection" />
            )}
            {activeCategory === 'faqs' && <ObjectionFaqList key="faqs" items={data.categories.faqs} kind="faq" />}
            {activeCategory === 'landmines' &&
              data.categories.landmines.map((item) => <LandmineRow key={item._id} item={item} />)}
            {activeCategory === 'competitors' &&
              data.categories.competitors.map((item) => <CompetitorRow key={item._id} item={item} />)}
            {activeCategory === 'playbooks' &&
              data.categories.playbooks.map((item) => <PlaybookRow key={item._id} item={item} />)}
          </div>
        </>
      )}
    </section>
  );
}
